import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { sheetLabelText, sheetNumberText, type CenterlineDocument } from "@centerline/core/viewer";
import { api, type DocumentSummary } from "./api";
import { freeCanvas, isTouchDevice, openPdf, sharpBudget, type PDFDocumentProxy, type PDFPageProxy } from "./pdf";
import { sharpRegion } from "./sharp";
import { href, navigate } from "./router";
import { alignMatrix, colorDiff, fitAlign, invertAlign, twoPointAlign, type Align, type Pt } from "./compare-math";
import "./compare.css";

/** Sheet overlay / compare: old revision (red) against new (blue). Read-only; never edits either plan set. */
type Mode = "diff" | "fade" | "split";
type Kind = "old" | "new" | "diff";
const MODES: Array<{ id: Mode; label: string; title: string }> = [
  { id: "diff", label: "Color diff", title: "Old in red, new in blue, unchanged linework dark" },
  { id: "fade", label: "Fade", title: "Opacity slider between old and new" },
  { id: "split", label: "Side by side", title: "Side by side, or a swipe divider" },
];
const MAX_Z = 16;

interface Side { meta: DocumentSummary; doc: CenterlineDocument }
interface Layer { stage: HTMLDivElement; base: HTMLCanvasElement | null; sharp: HTMLCanvasElement | null; sharpKey: string }
type View = { px: number; py: number; z: number };

function usePdf(id: string | undefined) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  useEffect(() => {
    setPdf(null);
    if (!id) return;
    let cancelled = false;
    const task = openPdf(api.planUrl(id));
    task.promise.then((p) => { if (!cancelled) setPdf(p); }).catch(() => {});
    return () => { cancelled = true; void task.destroy(); };
  }, [id]);
  return pdf;
}
function usePage(pdf: PDFDocumentProxy | null, n: number) {
  const [page, setPage] = useState<PDFPageProxy | null>(null);
  useEffect(() => {
    setPage(null);
    if (!pdf) return;
    let cancelled = false;
    pdf.getPage(Math.min(pdf.numPages, Math.max(1, n))).then((p) => { if (!cancelled) setPage(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [pdf, n]);
  return page;
}
function matchSheet(from: CenterlineDocument, index: number, to: CenterlineDocument): number {
  const want = sheetNumberText(from, index).trim().toLowerCase();
  const hit = to.plan.pages.findIndex((_, i) => sheetNumberText(to, i).trim().toLowerCase() === want);
  return hit >= 0 ? hit : Math.min(index, to.plan.pages.length - 1);
}

export function Compare(props: { n?: string; np?: number; o?: string; op?: number; mode?: string }) {
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [newSide, setNewSide] = useState<Side | null>(null);
  const [oldSide, setOldSide] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mode: Mode = props.mode === "fade" || props.mode === "split" ? props.mode : "diff"; // colour diff is the default
  const np = props.np ?? 1;
  const go = useCallback((patch: Partial<{ n: string; np: number; o: string; op: number; mode: Mode }>) => {
    const r = { name: "compare" as const, n: props.n, np: props.np, o: props.o, op: props.op, mode: props.mode, ...patch };
    navigate(r, true);
  }, [props.n, props.np, props.o, props.op, props.mode]);

  // New (frame) sheet, its project's plan sets, then a sensible default old set: the newest archived set, else any other.
  useEffect(() => {
    if (!props.n) { setError("Open Compare from a sheet in the viewer."); return; }
    let cancelled = false;
    api.document(props.n).then(async (r) => {
      if (cancelled) return;
      setNewSide({ meta: r.meta, doc: r.document });
      setProjectId(r.meta.projectId);
      const p = await api.project(r.meta.projectId);
      if (cancelled) return;
      setDocs(p.documents);
      if (!props.o) {
        const others = p.documents.filter((d) => d.id !== props.n && d.pageCount > 0);
        const pick = others.filter((d) => d.archivedAt).sort((a, b) => (b.archivedAt ?? "").localeCompare(a.archivedAt ?? ""))[0] ?? others[0];
        if (pick) go({ o: pick.id });
      }
    }).catch((e) => setError((e as Error).message));
    return () => { cancelled = true; };
  }, [props.n]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!props.o) { setOldSide(null); return; }
    let cancelled = false;
    api.document(props.o).then((r) => { if (!cancelled) setOldSide({ meta: r.meta, doc: r.document }); }).catch((e) => setError((e as Error).message));
    return () => { cancelled = true; };
  }, [props.o]);
  // Old sheet defaults to the same sheet number in the other set.
  useEffect(() => {
    if (oldSide && newSide && !props.op && oldSide.meta.id === props.o) go({ op: matchSheet(newSide.doc, np - 1, oldSide.doc) + 1 });
  }, [oldSide, newSide]); // eslint-disable-line react-hooks/exhaustive-deps
  const op = props.op ?? 1;

  const newPdf = usePdf(props.n), oldPdf = usePdf(props.o);
  const newPage = usePage(newPdf, np), oldPage = usePage(oldPdf, op);

  // ---- alignment ----
  const alignKey = `cl-compare-align:${props.o}:${op}:${props.n}:${np}`;
  const [align, setAlignState] = useState<Align | null>(null);
  const [manual, setManual] = useState(false);
  const [alignStep, setAlignStep] = useState<number>(-1); // -1 off; 0..3 = blue1, red1, blue2, red2
  const alignPts = useRef<Pt[]>([]);
  const [marks, setMarks] = useState<Array<{ p: Pt; c: "old" | "new" }>>([]);
  useEffect(() => {
    setAlignState(null); setManual(false); setAlignStep(-1); setMarks([]); alignPts.current = [];
    if (!oldPage || !newPage) return;
    const a = oldPage.getViewport({ scale: 1 }), b = newPage.getViewport({ scale: 1 });
    const saved = (() => { try { const s = JSON.parse(localStorage.getItem(alignKey) ?? "null"); return s && Number.isFinite(s.s) ? (s as Align) : null; } catch { return null; } })();
    setManual(!!saved);
    setAlignState(saved ?? fitAlign({ w: a.width, h: a.height }, { w: b.width, h: b.height }));
  }, [oldPage, newPage]); // eslint-disable-line react-hooks/exhaustive-deps
  const resetAlign = () => {
    if (!oldPage || !newPage) return;
    localStorage.removeItem(alignKey);
    const a = oldPage.getViewport({ scale: 1 }), b = newPage.getViewport({ scale: 1 });
    setAlignState(fitAlign({ w: a.width, h: a.height }, { w: b.width, h: b.height })); setManual(false); setAlignStep(-1); setMarks([]);
  };

  // ---- view (pan/zoom), applied straight to the DOM so gestures never re-render React ----
  const pane0 = useRef<HTMLDivElement>(null), pane1 = useRef<HTMLDivElement>(null), clipBox = useRef<HTMLDivElement>(null);
  const view = useRef<View>({ px: 0, py: 0, z: 0.2 });
  const [zoomPct, setZoomPct] = useState(20);
  const [fade, setFade] = useState(0.5);
  const [swipe, setSwipe] = useState(false);
  const swipeAt = useRef(0.5);
  const layers = useRef<Record<Kind, Layer> | null>(null);
  if (!layers.current) {
    const mk = (k: Kind): Layer => { const s = document.createElement("div"); s.className = `cmp-stage cmp-${k}`; return { stage: s, base: null, sharp: null, sharpKey: "" }; };
    layers.current = { old: mk("old"), new: mk("new"), diff: mk("diff") };
  }
  const frame = newPage ? newPage.getViewport({ scale: 1 }) : null;
  const W = frame?.width ?? 0, H = frame?.height ?? 0;
  const markEls = useRef<HTMLDivElement>(null);
  const settle = useRef<number>(0);

  const apply = useCallback(() => {
    const { px, py, z } = view.current, L = layers.current!;
    const t = `translate3d(${px}px, ${py}px, 0) scale(${z})`;
    for (const k of ["old", "new", "diff"] as Kind[]) L[k].stage.style.transform = t;
    const m = markEls.current;
    if (m) for (const el of Array.from(m.children) as HTMLElement[]) {
      const x = Number(el.dataset.x), y = Number(el.dataset.y);
      el.style.transform = `translate3d(${px + x * z}px, ${py + y * z}px, 0)`;
    }
    if (clipBox.current && pane0.current) clipBox.current.style.width = `${swipeAt.current * pane0.current.clientWidth}px`;
    window.clearTimeout(settle.current);
    settle.current = window.setTimeout(() => { setZoomPct(Math.round(z * 100)); void renderSharpRef.current(); }, 160);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fitView = useCallback(() => {
    const el = pane0.current;
    if (!el || !W) return;
    const vw = el.clientWidth, vh = el.clientHeight;
    const z = Math.max(0.01, Math.min((vw - 24) / W, (vh - 24) / H));
    view.current = { z, px: (vw - W * z) / 2, py: (vh - H * z) / 2 };
    apply();
  }, [W, H, apply]);
  useLayoutEffect(() => { fitView(); }, [W, H, mode, swipe]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const on = () => fitView(); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, [fitView]);

  const zoomAt = (sx: number, sy: number, nz: number) => {
    const v = view.current, el = pane0.current;
    const fitZ = el && W ? Math.min(el.clientWidth / W, el.clientHeight / H) : 0.05;
    nz = Math.max(fitZ * 0.5, Math.min(MAX_Z, nz));
    view.current = { z: nz, px: sx - (sx - v.px) * (nz / v.z), py: sy - (sy - v.py) * (nz / v.z) };
    apply();
  };
  const zoomBy = (f: number) => { const el = pane0.current; if (el) zoomAt(el.clientWidth / 2, el.clientHeight / 2, view.current.z * f); };

  // ---- rendering ----
  const kinds: Kind[] = mode === "diff" ? ["diff"] : ["old", "new"];
  const renderKind = useCallback(async (kind: Kind, k: number, rx: number, ry: number, cw: number, ch: number, isCancelled: () => boolean): Promise<HTMLCanvasElement | null> => {
    if (!oldPage || !newPage || !align) return null;
    const draw = async (which: "old" | "new") => {
      const c = document.createElement("canvas");
      c.width = Math.max(1, cw); c.height = Math.max(1, ch);
      const page = which === "old" ? oldPage : newPage;
      const m = which === "old" ? alignMatrix(align) : [1, 0, 0, 1, 0, 0];
      const ctx = c.getContext("2d", { willReadFrequently: kind === "diff" })!;
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
      try {
        await page.render({ canvasContext: ctx, viewport: page.getViewport({ scale: 1 }), transform: [k * m[0], k * m[1], k * m[2], k * m[3], k * m[4] - rx, k * m[5] - ry], background: "rgba(0,0,0,0)" }).promise;
      } catch { freeCanvas(c); return null; }
      return c;
    };
    if (kind !== "diff") return draw(kind);
    const a = await draw("old"); if (!a || isCancelled()) { if (a) freeCanvas(a); return null; }
    const b = await draw("new"); if (!b || isCancelled()) { freeCanvas(a); if (b) freeCanvas(b); return null; }
    const ia = a.getContext("2d")!.getImageData(0, 0, a.width, a.height), ib = b.getContext("2d")!.getImageData(0, 0, b.width, b.height);
    colorDiff(ia.data, ib.data, ib.data);
    b.getContext("2d")!.putImageData(ib, 0, 0);
    freeCanvas(a);
    return b;
  }, [oldPage, newPage, align]);

  // Base image of the whole sheet, inside a per-canvas budget (iPad Safari caps canvas memory).
  const baseK = useRef(1);
  const gen = useRef(0);
  useEffect(() => {
    const L = layers.current!;
    const myGen = ++gen.current;
    for (const k of ["old", "new", "diff"] as Kind[]) {
      const l = L[k];
      if (l.sharp) { l.sharp.remove(); freeCanvas(l.sharp); l.sharp = null; l.sharpKey = ""; }
      if (!kinds.includes(k) && l.base) { l.base.remove(); freeCanvas(l.base); l.base = null; }
    }
    if (!W || !align) return;
    const touch = isTouchDevice();
    const budget = touch ? 8_000_000 : 24_000_000, maxSide = touch ? 4096 : 12_000;
    const dpr = window.devicePixelRatio || 1;
    const k = Math.min(dpr * 1.5, Math.sqrt(budget / (W * H)), maxSide / Math.max(W, H));
    baseK.current = k;
    (async () => {
      for (const kind of kinds) {
        const c = await renderKind(kind, k, 0, 0, Math.round(W * k), Math.round(H * k), () => myGen !== gen.current);
        if (!c) continue;
        if (myGen !== gen.current) { freeCanvas(c); return; }
        Object.assign(c.style, { width: `${W}px`, height: `${H}px` }); c.className = "cmp-base";
        const l = L[kind];
        if (l.base) { l.base.remove(); freeCanvas(l.base); }
        l.stage.prepend(c); l.base = c;
      }
      void renderSharp();
    })();
  }, [renderKind, mode, W, H]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sharp overlay for what's on screen (same budget logic as the viewer, via sharp.ts).
  const sharpGen = useRef(0);
  const renderSharpRef = useRef<() => Promise<void>>(async () => {});
  const renderSharp = async () => {
    const el = pane0.current, L = layers.current!;
    if (!el || !W || !align) return;
    const { px, py, z } = view.current, dpr = window.devicePixelRatio || 1;
    const reg = sharpRegion({ w: W * z, h: H * z }, { x: -px, y: -py, w: el.clientWidth, h: el.clientHeight }, baseK.current / z, dpr, sharpBudget());
    const want = kinds;
    if (!reg) { for (const k of want) { const l = L[k]; if (l.sharp) { l.sharp.remove(); freeCanvas(l.sharp); l.sharp = null; l.sharpKey = ""; } } return; }
    const key = `${z}:${reg.x}:${reg.y}:${reg.w}:${reg.h}:${reg.scale}`;
    const myGen = ++sharpGen.current, g0 = gen.current;
    for (const kind of want) {
      const l = L[kind];
      if (l.sharpKey === key) continue;
      const c = await renderKind(kind, z * reg.scale, reg.x * reg.scale, reg.y * reg.scale, Math.round(reg.w * reg.scale), Math.round(reg.h * reg.scale), () => myGen !== sharpGen.current || g0 !== gen.current);
      if (!c) return;
      if (myGen !== sharpGen.current || g0 !== gen.current) { freeCanvas(c); return; }
      c.className = "cmp-sharp";
      Object.assign(c.style, { left: `${reg.x / z}px`, top: `${reg.y / z}px`, width: `${reg.w / z}px`, height: `${reg.h / z}px` });
      if (l.sharp) { l.sharp.remove(); freeCanvas(l.sharp); }
      l.stage.appendChild(c); l.sharp = c; l.sharpKey = key;
    }
  };
  renderSharpRef.current = renderSharp;
  useEffect(() => () => { const L = layers.current!; for (const l of Object.values(L)) { for (const c of [l.base, l.sharp]) if (c) freeCanvas(c); } }, []);

  // Put the stages in the right panes for the mode.
  useLayoutEffect(() => {
    const L = layers.current!, p0 = pane0.current, p1 = pane1.current, cb = clipBox.current;
    if (!p0) return;
    for (const l of Object.values(L)) l.stage.remove();
    for (const l of Object.values(L)) Object.assign(l.stage.style, { width: `${W}px`, height: `${H}px`, opacity: "" });
    const first = p0.firstChild;
    if (mode === "diff") p0.insertBefore(L.diff.stage, first);
    else if (mode === "fade") { p0.insertBefore(L.new.stage, first); p0.insertBefore(L.old.stage, first); }
    else if (swipe && cb) { p0.insertBefore(L.new.stage, first); cb.appendChild(L.old.stage); }
    else if (p1) { p0.insertBefore(L.old.stage, first); p1.insertBefore(L.new.stage, p1.firstChild); }
    apply();
  }, [mode, swipe, W, H]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mode === "fade") layers.current!.old.stage.style.opacity = String(1 - fade); }, [fade, mode]);

  // ---- gestures: one finger pans, two pinch, wheel/trackpad zooms; taps feed 2-point align ----
  const ptrs = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ x0: number; y0: number; t0: number; moved: boolean; pinch?: { d: number; z: number; mx: number; my: number; px: number; py: number } } | null>(null);
  const local = (e: { clientX: number; clientY: number }, el: HTMLElement) => { const r = el.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = e.currentTarget; el.setPointerCapture(e.pointerId);
    ptrs.current.set(e.pointerId, local(e, el));
    const pts = [...ptrs.current.values()];
    if (pts.length === 1) gesture.current = { x0: pts[0].x, y0: pts[0].y, t0: performance.now(), moved: false };
    else if (pts.length === 2 && gesture.current) {
      const [a, b] = pts, v = view.current;
      gesture.current.moved = true;
      gesture.current.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, z: v.z, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, px: v.px, py: v.py };
    }
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!ptrs.current.has(e.pointerId)) return;
    const prev = ptrs.current.get(e.pointerId)!, p = local(e, e.currentTarget);
    ptrs.current.set(e.pointerId, p);
    const g = gesture.current; if (!g) return;
    const pts = [...ptrs.current.values()];
    if (g.pinch && pts.length >= 2) {
      const [a, b] = pts, s = Math.hypot(a.x - b.x, a.y - b.y) / g.pinch.d;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, nz = Math.max(0.005, Math.min(MAX_Z, g.pinch.z * s)), r = nz / g.pinch.z;
      view.current = { z: nz, px: mx - (g.pinch.mx - g.pinch.px) * r, py: my - (g.pinch.my - g.pinch.py) * r };
      apply();
    } else if (pts.length === 1) {
      if (Math.hypot(p.x - g.x0, p.y - g.y0) > 6) g.moved = true;
      if (g.moved) { view.current = { ...view.current, px: view.current.px + p.x - prev.x, py: view.current.py + p.y - prev.y }; apply(); }
    }
  };
  const onUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const had = ptrs.current.delete(e.pointerId), g = gesture.current;
    if (ptrs.current.size === 0) {
      if (had && g && !g.moved && performance.now() - g.t0 < 600 && alignStep >= 0) { const p = local(e, e.currentTarget); tapAlign([(p.x - view.current.px) / view.current.z, (p.y - view.current.py) / view.current.z]); }
      gesture.current = null;
      if (g?.pinch) zoomAt(0, 0, view.current.z); // clamp
    } else if (g) { g.pinch = undefined; const q = [...ptrs.current.values()][0]; g.x0 = q.x; g.y0 = q.y; }
  };
  useEffect(() => {
    const els = [pane0.current, pane1.current].filter(Boolean) as HTMLDivElement[];
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement, r = el.getBoundingClientRect();
      const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? el.clientHeight : 1;
      zoomAt(e.clientX - r.left, e.clientY - r.top, view.current.z * Math.exp(-e.deltaY * unit * (e.ctrlKey ? 0.01 : 0.0015)));
    };
    for (const el of els) el.addEventListener("wheel", wheel, { passive: false });
    return () => { for (const el of els) el.removeEventListener("wheel", wheel); };
  }, [mode, swipe, W, H]); // eslint-disable-line react-hooks/exhaustive-deps

  const tapAlign = (fp: Pt) => {
    if (!align) return;
    const step = alignStep, pts = alignPts.current;
    pts[step] = step % 2 === 1 ? invertAlign(align, fp) : fp; // red taps are stored in old-sheet coordinates
    setMarks((m) => [...m.slice(0, step), { p: fp, c: step % 2 === 1 ? "old" : "new" }]);
    if (step < 3) { setAlignStep(step + 1); return; }
    const t = twoPointAlign(pts[1], pts[3], pts[0], pts[2]);
    setAlignStep(-1); setMarks([]); alignPts.current = [];
    if (!t) { setError("Those points are too close together. Pick two points far apart, like opposite corners of the title block."); return; }
    localStorage.setItem(alignKey, JSON.stringify(t)); setAlignState(t); setManual(true);
  };
  useLayoutEffect(() => { apply(); }, [marks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Swipe divider
  const onHandle = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const el = pane0.current; if (!el) return;
    const h = e.currentTarget; h.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => { const r = el.getBoundingClientRect(); swipeAt.current = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)); h.style.left = `${swipeAt.current * 100}%`; if (clipBox.current) clipBox.current.style.width = `${swipeAt.current * r.width}px`; };
    const up = () => { h.removeEventListener("pointermove", move); h.removeEventListener("pointerup", up); h.removeEventListener("pointercancel", up); };
    h.addEventListener("pointermove", move); h.addEventListener("pointerup", up); h.addEventListener("pointercancel", up);
  };

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest("input, select, textarea")) return;
      if (e.key === "0") fitView(); else if (e.key === "+" || e.key === "=") zoomBy(1.25); else if (e.key === "-") zoomBy(0.8);
      else if (e.key === "Escape") { setAlignStep(-1); setMarks([]); alignPts.current = []; }
    };
    window.addEventListener("keydown", key); return () => window.removeEventListener("keydown", key);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const sheetOptions = (s: Side | null) => s ? s.doc.plan.pages.map((_, i) => <option key={i} value={i + 1}>{sheetLabelText(s.doc, i)}</option>) : null;
  const docOptions = docs.map((d) => <option key={d.id} value={d.id}>{d.name}{d.working ? " (working set)" : ""}{d.archivedAt ? " (archived)" : ""}</option>);
  const loading = !newPage || (!!props.o && !oldPage);
  const STEP_TEXT = ["Tap a point on the BLUE (new) sheet — e.g. a title-block corner.", "Now tap the same point on the RED (old) sheet.", "Tap a second point on the BLUE sheet, far from the first.", "Tap the same second point on the RED sheet."];
  const swap = () => { if (props.o) go({ n: props.o, np: op, o: props.n, op: np }); };

  return (
    <div className={`compare mode-${mode}${mode === "split" ? (swipe ? " swipe" : " side") : ""}`}>
      <header className="toolbar">
        <a className="button ghost" href={props.n ? href({ name: "viewer", id: props.n, page: np }) : href(projectId ? { name: "project", id: projectId } : { name: "projects" })} title="Back to the sheet">←</a>
        <span className="doc-title">Compare</span>
        <div className="seg" role="tablist" aria-label="Compare mode">
          {MODES.map((m) => <button key={m.id} role="tab" aria-selected={mode === m.id} className={mode === m.id ? "on" : ""} title={m.title} onClick={() => go({ mode: m.id })}>{m.label}</button>)}
        </div>
        {mode === "fade" && <label className="cmp-fade"><span className="cmp-old-dot">Old</span><input type="range" min={0} max={1} step={0.01} value={fade} onChange={(e) => setFade(Number(e.target.value))} aria-label="Old / new opacity" /><span className="cmp-new-dot">New</span></label>}
        {mode === "split" && <div className="seg"><button className={!swipe ? "on" : ""} onClick={() => setSwipe(false)}>Side by side</button><button className={swipe ? "on" : ""} onClick={() => setSwipe(true)}>Swipe</button></div>}
        <span className="spacer" />
        <div className="group">
          <button onClick={() => zoomBy(0.8)} title="Zoom out (−)">−</button>
          <span className="zoom">{zoomPct}%</span>
          <button onClick={() => zoomBy(1.25)} title="Zoom in (+)">+</button>
          <button onClick={fitView} title="Fit (0)">Fit</button>
        </div>
        <button className={alignStep >= 0 ? "on" : ""} disabled={loading} onClick={() => { if (alignStep >= 0) { setAlignStep(-1); setMarks([]); } else { alignPts.current = []; setMarks([]); setAlignStep(0); } }} title="2-point align: tap two matching points on each sheet">Align</button>
        {manual && <button className="ghost" onClick={resetAlign} title="Back to auto-fit to page bounds">Reset align</button>}
      </header>
      <div className="cmp-pickers">
        <div className="cmp-pick old">
          <span className="cmp-chip old">OLD · red</span>
          <select value={props.o ?? ""} onChange={(e) => go({ o: e.target.value, op: undefined })} aria-label="Old plan set"><option value="" disabled>Pick a plan set…</option>{docOptions}</select>
          <select value={op} onChange={(e) => go({ op: Number(e.target.value) })} aria-label="Old sheet" disabled={!oldSide}>{sheetOptions(oldSide)}</select>
        </div>
        <button className="ghost cmp-swap" onClick={swap} disabled={!props.o} title="Swap old and new">⇄</button>
        <div className="cmp-pick new">
          <span className="cmp-chip new">NEW · blue</span>
          <select value={props.n ?? ""} onChange={(e) => go({ n: e.target.value, np: 1 })} aria-label="New plan set">{docOptions}</select>
          <select value={np} onChange={(e) => go({ np: Number(e.target.value) })} aria-label="New sheet" disabled={!newSide}>{sheetOptions(newSide)}</select>
        </div>
      </div>
      <div className="cmp-body">
        <div className="cmp-pane" ref={pane0} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className="cmp-clip" ref={clipBox} hidden={!(mode === "split" && swipe)} />
          {mode === "split" && swipe && <div className="cmp-handle" style={{ left: `${swipeAt.current * 100}%` }} onPointerDown={onHandle} role="slider" aria-label="Swipe divider" aria-valuenow={Math.round(swipeAt.current * 100)}><span>◀ ▶</span></div>}
          {mode === "split" && !swipe && <span className="cmp-pane-label old">OLD</span>}
          {mode === "split" && swipe && <><span className="cmp-pane-label old">OLD</span><span className="cmp-pane-label new right">NEW</span></>}
          <div className="cmp-marks" ref={markEls}>{marks.map((m, i) => <div key={i} className={`cmp-mark ${m.c}`} data-x={m.p[0]} data-y={m.p[1]}>{Math.floor(i / 2) + 1}</div>)}</div>
        </div>
        {mode === "split" && !swipe && <div className="cmp-pane" ref={pane1} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}><span className="cmp-pane-label new">NEW</span></div>}
        {loading && !error && <div className="cmp-loading">Opening sheets…</div>}
        {!props.o && newSide && docs.length > 0 && <div className="cmp-loading">This job has only one plan set. Upload the other revision to compare.</div>}
      </div>
      <footer className="statusbar">
        {alignStep >= 0 ? <span className="cmp-step">Align {alignStep + 1}/4 · {STEP_TEXT[alignStep]} <button className="linkish" onClick={() => { setAlignStep(-1); setMarks([]); }}>Cancel</button></span>
          : <span>{manual ? "Aligned by 2 points" : "Auto-fit to page bounds"}{align && Math.abs(align.s - 1) > 0.002 ? ` · old scaled ${(align.s * 100).toFixed(1)}%` : ""}{align && Math.abs(align.r) > 0.0005 ? ` · rotated ${(align.r * 180 / Math.PI).toFixed(2)}°` : ""}</span>}
        {mode === "diff" && <span className="cmp-legend"><i className="old" />Old only <i className="new" />New only <i className="same" />Unchanged</span>}
        {error && <span className="cmp-error">{error} <button className="linkish" onClick={() => setError(null)}>✕</button></span>}
      </footer>
    </div>
  );
}
